import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * INHALTSABSCHNITT (Auftrag 17.09.2026, Abschnitt 32; Abschnitte 24/25)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Karte, in der alles andere steht: Überschrift, darunter eine
 * Werkzeugleiste, darunter der Inhalt. Im Bestand gibt es dafür `.karte`
 * und `.karte-titel` mit `<div>`-Überschriften - für eine Vorlesehilfe ist
 * eine Seite dann eine einzige Fläche ohne Gliederung.
 *
 * Hier ist die Überschrift ein `<h2>` in einem `<section>` mit
 * `aria-labelledby`. Die Schriftgröße kommt aus `--schrift-abschnitt`
 * (Abschnitt 24), der Innenabstand aus dem 4/8-Raster (Abschnitt 25).
 *
 * `aufklappbar` ist für lange Seiten gedacht (die Anlagenseite hat neun
 * Abschnitte). Zugeklappt bleibt der Inhalt im Dokument NICHT stehen - er
 * wird ausgehängt, sonst hilft das Zuklappen der Übersicht, aber nicht der
 * Geschwindigkeit.
 */

export interface ContentSectionProps {
  titel?: string;
  beschreibung?: ReactNode;
  children: ReactNode;
  /** Suche, Filter, Umschalter - links unter der Überschrift. */
  werkzeuge?: ReactNode;
  /** Knöpfe rechts neben der Überschrift. */
  aktionen?: ReactNode;
  /** Kleine Angabe rechts, z. B. „Stand 17.09.2026". */
  meta?: ReactNode;
  aufklappbar?: boolean;
  /** Nur mit `aufklappbar`. Vorgabe offen. */
  standardOffen?: boolean;
  /** Ohne Rahmen und Polsterung - wenn der Inhalt eine eigene Karte ist. */
  nackt?: boolean;
  id?: string;
  className?: string;
}

export function ContentSection({
  titel, beschreibung, children, werkzeuge, aktionen, meta,
  aufklappbar = false, standardOffen = true, nackt = false, id, className,
}: ContentSectionProps) {
  const eigeneId = useId();
  const titelId = `${id ?? eigeneId}-titel`;
  const inhaltId = `${id ?? eigeneId}-inhalt`;
  const [offen, setOffen] = useState(standardOffen);

  const zeigen = !aufklappbar || offen;

  return (
    <section
      id={id}
      className={[
        'm-abschnitt', nackt ? 'm-abschnitt-nackt' : '', className ?? '',
      ].filter(Boolean).join(' ')}
      aria-labelledby={titel ? titelId : undefined}
    >
      {(titel || aktionen || meta) && (
        <div className="m-abschnitt-kopf">
          <div className="m-abschnitt-kopf-links">
            {titel && (aufklappbar ? (
              <h2 className="m-abschnitt-titel" id={titelId}>
                <button
                  type="button"
                  className="m-abschnitt-klappe"
                  aria-expanded={offen}
                  aria-controls={inhaltId}
                  onClick={() => setOffen((o) => !o)}
                >
                  <span className="m-abschnitt-klappzeichen" aria-hidden="true">
                    {offen ? '▾' : '▸'}
                  </span>
                  {titel}
                </button>
              </h2>
            ) : (
              <h2 className="m-abschnitt-titel" id={titelId}>{titel}</h2>
            ))}
            {beschreibung && (
              <p className="m-abschnitt-beschreibung">{beschreibung}</p>
            )}
          </div>
          <div className="m-abschnitt-kopf-rechts">
            {meta && <span className="m-abschnitt-meta">{meta}</span>}
            {aktionen && <div className="m-abschnitt-aktionen">{aktionen}</div>}
          </div>
        </div>
      )}

      {zeigen && werkzeuge && (
        <div className="m-abschnitt-werkzeuge">{werkzeuge}</div>
      )}

      {zeigen && (
        <div className="m-abschnitt-inhalt" id={inhaltId}>{children}</div>
      )}
    </section>
  );
}
